// THE ADDRESS BOX ON `/`, WITH APPLE'S SUGGESTIONS (WEB-SUGGEST, 2026-09-29; Tyler: "address should auto populate on
// this initial page").
//
// WAI-ARIA APG: a combobox with a listbox popup and list autocomplete, the same pattern and keyboard as the site's free
// check — ArrowDown / ArrowUp walk the list, Enter chooses the highlighted suggestion (and otherwise submits the form, as
// any address field does), Escape and Tab close it. The listbox sits OUTSIDE the <label>, so the box's accessible name
// stays "Your property address" and never grows the suggestions' text.
//
// WHEN SUGGESTIONS ARE OFF — no suggester, the token function's refusal, Apple's script or MapKit JS failing, a cap —
// this is the plain text box it always was: the combobox role and every attribute that promised a list go, so a screen
// reader announces a text field and nothing more.
//
// WHEN APPLE'S SCRIPT LOADS: on focus; or, with `warm` (the box on `/`, WALK-FIX-2), as soon as the box appears.
//
// WHAT IT NEVER DOES. It never searches the text the box OPENED with (a free-check hand-in, or what this tab last sent):
// suggestions follow the owner's own keystrokes only, so a prefill stays exactly as it arrived. It never submits the
// form. And a pick never wipes the Unit box (`unitAfterPick`, R162).
//
// Every word on it is the page's existing words; this component adds no sentence. The one line it can show under the box,
// `hint`, is the caller's (WEB-PARITY-2B: the app's "Not in the list?" line), shown as the app shows it — while the box
// has focus and holds at least six characters (AddressAutocompleteField.swift:131, `focused && address.trimmed.count >= 6`).

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { keepUnitInLine, unitAfterPick, type AddressSuggester, type SuggestItem } from "../services/addressSuggest";

export interface AddressComboboxProps {
  readonly suggester: AddressSuggester | null;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  onChange(value: string): void;
  /** The Unit box beside it: read and written only by a pick, under `unitAfterPick`. With none (a one-line address such
   *  as the mailing address on `/you`), a unit the owner typed stays in the line after a pick (`keepUnitInLine`). */
  readonly unit?: string;
  onUnitChange?(unit: string): void;
  /** A line under the box while it has focus and holds six characters or more; none when absent. */
  readonly hint?: ReactNode;
  /** WEB-PARITY-5: told the address a pick resolved to — Apple's one-line address, or the suggestion's own label where
   *  that never comes — so a form can fill its fields from it, as the app's contact sheet does (`applyCompletion`,
   *  OwnerProfileEditor.swift:116-128). Absent, a pick only fills this box, as before. */
  onPick?(line: string): void;
  /** WALK-FIX-2: load MapKit JS as soon as the box appears, not on focus, so the first suggestions are not waiting on
   *  Apple's script (`/`'s box; Tyler, 2026-10-05: "the address auto populate is a little laggy"). Nothing is searched
   *  until the owner types. */
  readonly warm?: boolean;
}

export function AddressCombobox(props: AddressComboboxProps) {
  const { suggester, value, unit, onChange, onUnitChange, warm = false } = props;
  const [on, setOn] = useState(() => suggester !== null && suggester.state() !== "off");
  const [items, setItems] = useState<SuggestItem[]>([]);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  /** Bumped by every keystroke and every pick, so a completion that arrives after the owner moved on is dropped. */
  const edits = useRef(0);

  const base = useId();
  const labelId = `${base}-label`;
  const listId = `${base}-list`;
  const optionId = (i: number) => `${base}-opt-${i}`;
  const open = on && items.length > 0;

  // Whether suggestions are on for this page: the token function is asked once (`prepare`), and the box turns plain the
  // moment anything about them fails.
  useEffect(() => {
    if (!suggester) return;
    const plain = () => {
      setOn(false);
      setItems([]);
      setActive(-1);
    };
    const unsubscribe = suggester.onOff(plain);
    if (suggester.state() === "off") plain();
    // Warm: the token and MapKit JS now (`start` asks `prepare` first). Otherwise the token only, and the script on focus.
    else void (warm ? suggester.start() : suggester.prepare());
    return () => {
      unsubscribe();
      suggester.cancel();
    };
  }, [suggester, warm]);

  const show = (next: SuggestItem[]) => {
    // Drawn only while the box still has focus: an answer that lands after the owner left is not shown.
    const focused = input.current !== null && document.activeElement === input.current;
    setItems(focused ? next : []);
    setActive(-1);
  };

  const close = () => {
    suggester?.cancel();
    setItems([]);
    setActive(-1);
  };

  const choose = (i: number) => {
    const item = items[i];
    if (!item || !suggester) return;
    // The Unit box first, from what the owner had typed: never wiped; a unit named in the text moves into an empty box.
    // With no Unit box, the typed unit stays in the line instead.
    const typed = value;
    const line = onUnitChange ? (l: string) => l : (l: string) => keepUnitInLine(l, typed);
    if (onUnitChange) onUnitChange(unitAfterPick(value, unit ?? ""));
    const picked = line(suggester.fill(item));
    edits.current += 1;
    const mine = edits.current;
    onChange(picked);
    close();
    input.current?.focus();
    // Apple's display lines are in the box at once; the whole one-line address (with its ZIP) replaces them when it
    // comes — unless the owner has typed or picked again in the meantime.
    void suggester.complete(item).then((full) => {
      if (edits.current !== mine) return;
      if (full) onChange(line(full));
      props.onPick?.(line(full || picked));
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!on || !suggester) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        suggester.suggest(value, show);
        return;
      }
      const n = items.length;
      if (e.key === "ArrowDown") setActive((a) => (a + 1) % n);
      else setActive((a) => (a <= 0 ? n - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (open && active >= 0) {
        e.preventDefault();
        choose(active);
      }
      // Otherwise Enter submits the form, as it does in any address field.
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        close();
      }
    } else if (e.key === "Tab") {
      close();
    }
  };

  const combobox = on
    ? {
        role: "combobox",
        "aria-autocomplete": "list" as const,
        "aria-expanded": open,
        "aria-controls": listId,
        "aria-activedescendant": open && active >= 0 ? optionId(active) : undefined,
      }
    : {};

  // The hint sits AFTER the combo, so the suggestions (positioned under the combo) draw over it rather than below it.
  return (
    <>
      <div className="combo">
        <label className="field">
          <span id={labelId}>{props.label}</span>
          <input
            ref={input}
            type="text"
            value={value}
            autoComplete="off"
            placeholder={props.placeholder}
            {...combobox}
            onFocus={() => {
              setFocused(true);
              // MapKit JS is loaded from Apple now if it is not already (a `warm` box loaded it when it appeared). Nothing
              // is searched on focus.
              if (on && suggester) void suggester.start();
            }}
            onChange={(e) => {
              const next = e.currentTarget.value;
              edits.current += 1;
              onChange(next);
              if (on && suggester) suggester.suggest(next, show);
            }}
            onBlur={() => {
              setFocused(false);
              close();
            }}
            onKeyDown={onKeyDown}
          />
        </label>
        {on && (
          <ul id={listId} role="listbox" aria-labelledby={labelId} className="suggestions" hidden={!open}>
            {items.map((item, i) => (
              <li
                key={`${i}:${item.label}`}
                id={optionId(i)}
                role="option"
                aria-selected={i === active}
                className={i === active ? "is-active" : undefined}
                // mousedown, not click: focus stays in the box, so the list does not close before the choice lands.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(i);
                }}
              >
                {item.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {props.hint !== undefined && focused && value.trim().length >= 6 && <p className="combo-hint">{props.hint}</p>}
    </>
  );
}
